import { type Dispatch, type SetStateAction, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Input } from "@volca/ui/components/ui/input";
import { Label } from "@volca/ui/components/ui/label";
import {
  PageContainer,
  PageHeader,
} from "@volca/ui/components/ui/page-components";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { toast } from "@volca/ui/lib/toast";
import { useBreadcrumbs } from "@/app-shell";
import { DocumentChip } from "./document-chip";
import { DOCUMENT_STATUS } from "./document-constants";
import { useReportDocuments } from "./query";
import {
  ReportDocumentsPicker,
  useReportDocumentIds,
} from "./report-documents-picker";
import { useCreateReportDefinition } from "./report-query";
import { PROFITABILITY_PATH, profitabilityTemplatePath } from "./routes";
import { reportError } from "./utils";

const DocumentGroup = ({
  businessId,
  label,
  hint,
  testId,
  documentIds,
  onDocumentIdsChange,
}: {
  businessId: string;
  label: string;
  hint: string;
  testId: string;
  documentIds: string[];
  onDocumentIdsChange: Dispatch<SetStateAction<string[]>>;
}) => {
  const query = useReportDocuments(businessId, documentIds);
  const documents = query.data ?? [];
  const picker = useReportDocumentIds(
    businessId,
    documentIds,
    documents,
    onDocumentIdsChange,
  );
  return (
    <Stack className="w-full" align="start" gap={3} data-testid={testId}>
      <Label>{label}</Label>
      <Text tone="muted">{hint}</Text>
      <Stack direction="row" gap={3} className="flex-wrap" align="center">
        <ReportDocumentsPicker
          uploading={picker.uploading}
          onFiles={picker.handleFiles}
        />
        {documents.map((document) => (
          <DocumentChip
            key={document.id}
            document={document}
            onRemove={() => picker.removeDocument(document.id)}
          />
        ))}
      </Stack>
    </Stack>
  );
};

export const ReportBuilder = ({ businessId }: { businessId: string }) => {
  const navigate = useNavigate();
  useBreadcrumbs([
    { label: "Job Costing", href: PROFITABILITY_PATH },
    { label: "New agent" },
  ]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sampleIds, setSampleIds] = useState<string[]>([]);
  const [goalIds, setGoalIds] = useState<string[]>([]);
  const create = useCreateReportDefinition(businessId);
  const samples = useReportDocuments(businessId, sampleIds);
  const goals = useReportDocuments(businessId, goalIds);
  const unreadable = [...(samples.data ?? []), ...(goals.data ?? [])].filter(
    (document) => document.status === DOCUMENT_STATUS.failed,
  );
  const ready =
    name.trim().length > 0 &&
    description.trim().length > 0 &&
    sampleIds.length > 0 &&
    unreadable.length === 0;

  return (
    <PageContainer>
      <Stack align="start" gap={6} className="w-full max-w-3xl">
        <PageHeader
          title="New agent"
          description="Describe your report and add the files it reads. Volca builds an agent you can reuse."
        />
        <Stack className="w-full" align="start" gap={1}>
          <Label htmlFor="report-name">Name</Label>
          <Input
            id="report-name"
            value={name}
            placeholder="GM Tracker"
            onChange={(event) => setName(event.target.value)}
          />
        </Stack>
        <Stack className="w-full" align="start" gap={1}>
          <Label htmlFor="report-description">
            What the report should show
          </Label>
          <Textarea
            id="report-description"
            value={description}
            rows={8}
            placeholder={
              "For each department: net revenue, total labor with 20% burden, materials, idle payroll, labor %, materials %, GM %. " +
              "Business units map to departments with the lookup on the GM Summary sheet…"
            }
            onChange={(event) => setDescription(event.target.value)}
          />
          <Text tone="muted">
            Say which columns to use, how to compute each figure, and anything
            that is entered by hand. The more precise, the fewer surprises.
          </Text>
        </Stack>
        <DocumentGroup
          businessId={businessId}
          label="Documents the report reads"
          hint="One of each file the report needs, as you will upload them each time: exports, statements, logs."
          testId="sample-documents"
          documentIds={sampleIds}
          onDocumentIdsChange={setSampleIds}
        />
        <DocumentGroup
          businessId={businessId}
          label="The result you expect (optional)"
          hint="A spreadsheet with the numbers you computed yourself for these documents. Volca checks its figures against it."
          testId="goal-documents"
          documentIds={goalIds}
          onDocumentIdsChange={setGoalIds}
        />
        {unreadable.length > 0 && (
          <Text className="text-danger">
            {unreadable.map((document) => document.file_name).join(", ")} could
            not be read. Remove it or upload a readable copy.
          </Text>
        )}
        <Stack align="start" direction="row" gap={2}>
          <Button
            disabled={!ready || create.isPending}
            aria-busy={create.isPending}
            onClick={() =>
              create.mutate(
                {
                  name: name.trim(),
                  description: description.trim(),
                  sample_document_ids: sampleIds,
                  goal_document_ids: goalIds,
                },
                {
                  onSuccess: (report) => {
                    toast.success("Volca is writing the agent", {
                      description:
                        "It reads the documents and checks its numbers. This usually takes a few minutes.",
                    });
                    void navigate(profitabilityTemplatePath(report.id));
                  },
                  onError: (error) =>
                    toast.error("The agent could not be created", {
                      description: reportError(error),
                    }),
                },
              )
            }
          >
            Build the agent
          </Button>
          <Button variant="ghost" onClick={() => navigate(PROFITABILITY_PATH)}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </PageContainer>
  );
};
